/**
 * useGameInitialization — Game lifecycle: load, start systems, cleanup
 *
 * Handles:
 * - Debug helpers (window.clearGame, window.toggleDebugMenu)
 * - saveManager.load() + offline progress detection
 * - initializeShopsAndQuests()
 * - System startup (autosave, buddies, hauler, stats, mentor, session goals)
 * - GameLoop registration and start
 */

import { useEffect } from 'react';
import {
  gameLoop,
  saveManager,
  fishingEngine,
  buddyManager,
  haulerManager,
  statTracker,
  mentorManager,
  sessionGoalManager,
  trapManager,
  initializeShopsAndQuests,
  cleanupShopsAndQuests,
  calculateOfflineProgress,
} from '@/systems';
import { useGameStore } from '@/state';
import { DEBUG_ENABLED, DEBUG_SHORTCUT } from '@/config/debug';
import type { OfflineReport } from '@/systems/offline';

export function useGameInitialization(
  setOfflineReport: (report: OfflineReport | null) => void,
  setDebugMenuVisible: React.Dispatch<React.SetStateAction<boolean>>,
): void {
  useEffect(() => {
    // Initialize game systems
    console.log('🎮 Initializing Fishing Empire...');

    // Add debug helpers to window (only if DEBUG_ENABLED)
    if (DEBUG_ENABLED) {
      window.clearGame = () => {
        localStorage.clear();
        console.log('🗑️ localStorage cleared! Refresh the page (F5) to start fresh.');
      };

      window.toggleDebugMenu = () => {
        setDebugMenuVisible((prev) => !prev);
        console.log('🔧 Debug menu toggled');
      };

      console.log(
        `🔧 Debug mode enabled! Press ${DEBUG_SHORTCUT.ctrl ? 'Ctrl+' : ''}${DEBUG_SHORTCUT.shift ? 'Shift+' : ''}${DEBUG_SHORTCUT.alt ? 'Alt+' : ''}${DEBUG_SHORTCUT.key} to toggle debug menu`
      );
    }

    // Load saved game from localStorage
    saveManager.load();

    // Check for offline progress
    const lastSavedAt = useGameStore.getState().playerState.lastSavedAt;
    if (lastSavedAt) {
      const elapsedMs = Date.now() - lastSavedAt;
      const MIN_OFFLINE_MS = 5 * 60 * 1000;
      if (elapsedMs >= MIN_OFFLINE_MS) {
        const report = calculateOfflineProgress(
          elapsedMs,
          useGameStore.getState().playerState
        );
        if (report.fishToInventory > 0 || report.goldEarned > 0 || report.xpEarned > 0) {
          setOfflineReport(report);
        }
      }
    }

    // Initialize shops and quests system
    initializeShopsAndQuests();

    // Start autosave
    saveManager.startAutosave();

    // Start buddy auto-fishing
    buddyManager.start();

    // Start hauler auto-selling
    haulerManager.start();

    // Start stat tracking (fish/hr, $/hr)
    statTracker.start();

    // Start mentor bonus activity tracking
    mentorManager.start();

    // Start session goals (daily objectives)
    sessionGoalManager.start();

    // Initialize trap manager (tick timer + offline state recovery)
    trapManager.initialize();

    // Register game loop update callback
    const unregisterLoop = gameLoop.register((deltaTime) => {
      // Update fishing engine
      fishingEngine.update(deltaTime);
      // Buddy manager uses setInterval, so no update needed here
    });

    // Start game loop
    gameLoop.start();

    // Cleanup on unmount
    return () => {
      gameLoop.stop();
      saveManager.stopAutosave();
      buddyManager.stop();
      haulerManager.stop();
      statTracker.stop();
      mentorManager.stop();
      sessionGoalManager.stop();
      trapManager.cleanup();
      cleanupShopsAndQuests();
      unregisterLoop();
    };
  }, []);
}
